import React, { useState, useRef } from "react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { IconVariant } from "@/utils/fontAwesomeUtils";
import { Tooltip } from "@chakra-ui/react";

type Props = {
	icon: string;
	iconSize: number;
	iconVariant: IconVariant;
	label: string;
	onClick: () => void;
	dataTestId: string;
};

export function ClassroomTileButton(props: Props): JSX.Element {
	// Bit of complicated state management here to handle the tooltip visibility.
	// The tooltip is shown on hover and focus by default. When the modal closes,
	// focus returns to the button, which shows the tooltip. We don't want the
	// tooltip to show when the modal closes, so we need to handle this manually.
	// The tooltip should still be shown when manually focused on the button via keyboard.

	const [showTooltip, setShowTooltip] = useState(false);
	const clickedRef = useRef(false);

	const handleClick = () => {
		// Mark that the button was just clicked
		clickedRef.current = true;
		props.onClick();
	};

	const handleFocus = () => {
		// Only show tooltip on focus if it wasn't from a recent click
		// When modal closes, focus returns after the click
		// When user tabs with keyboard, this flag will be false
		if (!clickedRef.current) {
			setShowTooltip(true);
		}
		// Reset the flag after checking it, so next focus (e.g., keyboard tab) will work
		clickedRef.current = false;
	};

	const handleBlur = () => {
		// Hide tooltip when button loses focus
		setShowTooltip(false);
	};

	return (
		<Tooltip placement="top" hasArrow label={props.label} isOpen={showTooltip}>
			<button
				aria-label={props.label}
				className="classroom_tile_button"
				onClick={handleClick}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onMouseEnter={() => setShowTooltip(true)}
				onMouseLeave={() => setShowTooltip(false)}
				data-testid={props.dataTestId}
			>
				<ChakraFAIcon icon={props.icon} width={props.iconSize} variant={props.iconVariant} />
			</button>
		</Tooltip>
	);
}
